import React, { useEffect, useRef, useState, forwardRef, useMemo } from "react";
import { MenuList, MenuItem, Divider } from "@mui/material";
import {
	CustomIconButton,
	CustomTableRow,
	CustomTableCell,
	CustomButton,
	LimitedTextField,
	CustomDialog,
	icon,
	DropDown,
	IconComponent,
} from "../../../../common/components";
import {
	isFeatureEnabled,
	isSegFeatureEnabled,
	SegFlags,
	translate,
	translateDate,
} from "../../../../common/providers";
import { ApiService, CoveragePhaseService } from "../../../../api";
import { debounce } from "../../../../common/utils";
import styles from "./PhaseRow.module.css";
import { FLAGS } from "../../../../common/providers/feature-flag-provider";

const PhaseRow = forwardRef(
	(
		{
			className,
			isEditable,
			onClickArchive,
			onClickExport,
			onClickImportHeritage,
			onClickDelete,
			onClickHistory,
			onClickLock,
			onClickOpenAssociated,
			onSave,
			onCancel,
			phase,
			projectId,
			rowOrder,
			isArchivedTab = false,
			provided,
			editingPhaseId = null,
			onStartInlineEdit,
			onEndInlineEdit,
		},
		ref
	) => {
		const [openDropdown, setOpenDropdown] = useState(false);
		const [isEditing, setIsEditing] = useState(false);
		const [editedPhase, setEditedPhase] = useState({});
		const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
		const [isNameDuplicate, setIsNameDuplicate] = useState(false);
		const [isNameChecking, setIsNameChecking] = useState(false);
		const [isDateRangeInvalid, setIsDateRangeInvalid] = useState(false);
		const [confirmationDialog, setConfirmationDialog] = useState({
			open: false,
			type: null,
			title: "",
			message: "",
			onConfirm: null,
		});
		const anchorRef = useRef(null);
		const cancelTokenRef = useRef(null);

		const debouncedCheckName = useMemo(
			() =>
				debounce(async (trimmedName) => {
					if (cancelTokenRef.current) {
						ApiService.cancelTokens(cancelTokenRef.current);
					}
					cancelTokenRef.current = ApiService.getCancelTokenSource();
					CoveragePhaseService.existsByName({ projectId, name: trimmedName }, cancelTokenRef.current.token)
						.then((exists) => {
							setIsNameDuplicate(!!exists.value);
						})
						.catch(() => {
							setIsNameDuplicate(false);
						})
						.finally(() => setIsNameChecking(false));
				}, 400),
			[projectId]
		);

		const inlineInputSx = {
			"& .MuiOutlinedInput-root": {
				height: "24px",
				minHeight: "24px",
				fontSize: "var(--font-size-xs)",
				borderRadius: "2px",
				backgroundColor: "var(--color-white)",
				"& input, & textarea": {
					padding: "1px var(--spacing-2xs)",
					height: "auto",
					lineHeight: 1.2,
					fontSize: "var(--font-size-xs)",
					boxSizing: "border-box",
				},
				"&:hover .MuiOutlinedInput-notchedOutline": {
					borderColor: "var(--grey-400)",
				},
				"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					borderColor: "var(--blue-500)",
					borderWidth: "2px",
				},
				"& .MuiOutlinedInput-notchedOutline": {
					borderColor: "var(--grey-200)",
					borderWidth: "1px",
				},
			},
			"& .MuiInputLabel-root": {
				display: "none",
			},
		};

		const multilineInputSx = {
			...inlineInputSx,
			"& .MuiOutlinedInput-root": {
				...inlineInputSx["& .MuiOutlinedInput-root"],
				height: "auto",
				minHeight: "40px",
				padding: 0,
				"& textarea": {
					padding: "1px var(--spacing-2xs)",
					minHeight: "36px",
					fontSize: "var(--font-size-xs)",
				},
			},
		};

		const dateInputSx = {
			...inlineInputSx,
			// eslint-disable-next-line
			'& input[type="date"]': {
				fontFamily: "inherit",
				fontSize: "var(--font-size-xs)",
				padding: "1px var(--spacing-2xs)",
				height: "auto",
			},
			"& .MuiOutlinedInput-root": {
				...inlineInputSx["& .MuiOutlinedInput-root"],
				"&.Mui-error .MuiOutlinedInput-notchedOutline": {
					borderColor: "var(--red-500) !important",
					borderWidth: "1px",
				},
				"&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
					borderColor: "var(--red-500) !important",
				},
				"&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
					borderColor: "var(--red-500) !important",
					borderWidth: "2px",
				},
			},
		};

		const handleToggleDropdown = () => {
			if (phase?.locked) {
				return;
			}
			setOpenDropdown(!openDropdown);
		};

		const handleCloseDropdown = () => {
			setOpenDropdown(false);
		};

		const showConfirmationDialog = (type, onConfirm) => {
			let title;
			let message;

			switch (type) {
				case "archive":
					title = translate("project-phases.confirm-archive-title");
					message = translate("project-phases.confirm-archive-message");
					break;
				case "unarchive":
					title = translate("project-phases.confirm-unarchive-title");
					message = translate("project-phases.confirm-unarchive-message");
					break;
				case "delete":
					title = translate("project-phases.confirm-delete-title");
					message = translate("project-phases.confirm-delete-message");
					break;
				case "lock":
					title = translate("project-phases.confirm-lock-title");
					message = translate("project-phases.confirm-lock-message");
					break;
				case "unlock":
					title = translate("project-phases.confirm-unlock-title");
					message = translate("project-phases.confirm-unlock-message");
					break;
				default:
					title = translate("project-phases.phase-row.confirm");
					message = translate("project-phases.phase-row.confirm-action");
			}

			setConfirmationDialog({
				open: true,
				type,
				title,
				message,
				onConfirm,
			});
		};

		const closeConfirmationDialog = () => {
			setConfirmationDialog({
				open: false,
				type: null,
				title: "",
				message: "",
				onConfirm: null,
			});
		};

		const handleConfirmAction = () => {
			if (confirmationDialog.onConfirm) {
				confirmationDialog.onConfirm();
			}
			closeConfirmationDialog();
		};

		const formatDateForHtmlInput = (dateValue) => {
			if (!dateValue) {
				return "";
			}
			try {
				const date = new Date(dateValue);
				if (Number.isNaN(date.getTime())) {
					return "";
				}
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, "0");
				const day = String(date.getDate()).padStart(2, "0");

				return `${year}-${month}-${day}`;
			} catch (error) {
				return "";
			}
		};

		const handleEdit = () => {
			if (onStartInlineEdit && !onStartInlineEdit(phase.id)) {
				return;
			}
			setIsEditing(true);
			const startDate = formatDateForHtmlInput(phase?.startDate);
			const endDate = formatDateForHtmlInput(phase?.endDate);
			setEditedPhase({
				id: phase.id,
				name: phase.name || "",
				description: phase.description || "",
				startDate,
				endDate,
			});
			if (startDate && endDate) {
				const start = new Date(startDate);
				const end = new Date(endDate);
				setIsDateRangeInvalid(end < start);
			} else {
				setIsDateRangeInvalid(false);
			}
			handleCloseDropdown();
		};

		const formatDateForJava = (dateString) => {
			if (!dateString) {
				return null;
			}
			try {
				return `${dateString}T00:00:00`;
			} catch (error) {
				console.error("Error formatting date:", error);
				return null;
			}
		};

		const handleSave = () => {
			if (onSave) {
				const completePhase = {
					...phase,
					...editedPhase,
					startDate: formatDateForJava(editedPhase.startDate),
					endDate: formatDateForJava(editedPhase.endDate),
				};
				onSave(completePhase);
			}
			setIsEditing(false);
			if (onEndInlineEdit) {
				onEndInlineEdit();
			}
		};

		const handleCancel = () => {
			setIsEditing(false);
			setEditedPhase({});
			if (onEndInlineEdit) {
				onEndInlineEdit();
			}
			if (onCancel) {
				onCancel();
			}
		};

		const validateDateRange = (startDate, endDate) => {
			if (!startDate || !endDate) {
				return true;
			}
			const start = new Date(startDate);
			const end = new Date(endDate);
			return end >= start;
		};

		const handleInputChange = (field, value) => {
			setEditedPhase((prev) => {
				const updated = {
					...prev,
					[field]: value,
				};

				if (field === "startDate" || field === "endDate") {
					const isValid = validateDateRange(updated.startDate, updated.endDate);
					setIsDateRangeInvalid(!isValid);
				}

				return updated;
			});
		};

		useEffect(() => {
			if (!isEditing || !projectId) {
				setIsNameDuplicate(false);
				setIsNameChecking(false);
			} else {
				const trimmed = editedPhase.name?.trim();

				if (!trimmed || trimmed.length === 0) {
					setIsNameDuplicate(false);
					setIsNameChecking(false);
				} else if (trimmed === phase.name?.trim()) {
					setIsNameDuplicate(false);
					setIsNameChecking(false);
				} else {
					setIsNameChecking(true);
					setIsNameDuplicate(false);
					debouncedCheckName(trimmed);
				}
			}

			return () => {
				if (cancelTokenRef.current) {
					ApiService.cancelTokens(cancelTokenRef.current);
				}
			};
		}, [editedPhase.name, isEditing, phase.name, projectId, debouncedCheckName]);

		const handleDelete = () => {
			onClickDelete(phase);
			handleCloseDropdown();
		};

		const handleArchive = () => {
			const actionType = isArchivedTab || phase.isArchived || phase.archived ? "unarchive" : "archive";
			showConfirmationDialog(actionType, () => {
				onClickArchive(phase);
			});
			handleCloseDropdown();
		};

		const handleExport = () => {
			onClickExport(phase);
		};

		const handleImportHeritage = () => {
			if (typeof onClickImportHeritage === "function") {
				onClickImportHeritage(phase);
			}
			handleCloseDropdown();
		};

		const handleLock = () => {
			const actionType = phase.locked ? "unlock" : "lock";
			showConfirmationDialog(actionType, () => {
				onClickLock(phase);
			});
			handleCloseDropdown();
		};

		const lockButtonClassName = phase.locked ? styles.lockButton : styles.unlockedButton;
		const lockIcon = phase.locked ? icon.faLock : icon.faLockOpen;
		const lockIconColor = phase.locked ? "var(--red-500)" : undefined;
		const lockTooltip = phase.locked ? translate("project-phases.unlock") : translate("project-phases.lock");

		const renderExpandableText = (text, maxLength = 70) => {
			if (!text || text.length <= maxLength) {
				return text || "-";
			}

			const handleKeyDown = (event, action) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					action();
				}
			};

			if (isDescriptionExpanded) {
				return (
					<span className={styles.expandableText}>
						{text}
						<button
							aria-label={translate("project-phases.phase-row.view-less")}
							className={styles.viewMoreLink}
							type="button"
							onClick={() => setIsDescriptionExpanded(false)}
							onKeyDown={(e) => handleKeyDown(e, () => setIsDescriptionExpanded(false))}
						>
							{translate("project-phases.phase-row.view-less")}
						</button>
					</span>
				);
			}

			return (
				<span className={styles.expandableText}>
					{text.substring(0, maxLength)}...
					<button
						aria-label={translate("project-phases.phase-row.view-more")}
						className={styles.viewMoreLink}
						type="button"
						onClick={() => setIsDescriptionExpanded(true)}
						onKeyDown={(e) => handleKeyDown(e, () => setIsDescriptionExpanded(true))}
					>
						{translate("project-phases.phase-row.view-more")}
					</button>
				</span>
			);
		};
		if (isEditing) {
			return (
				<>
					<CustomTableRow
						className={`${styles.editingRow} ${phase.locked ? styles.lockedRow : ""}`}
						data-testid={`phase.row.${phase?.id}`}
					>
						<CustomTableCell
							className={styles.orderNameCell}
							data-testid={`phase.name.cell.${phase?.name}`}
						>
							<div className={styles.flexContainer}>
								<LimitedTextField
									fullWidth
									containerClassName={styles.inlineInputContainer}
									error={isNameDuplicate}
									maxLength={256}
									placeholder=""
									showCounter={false}
									showHelper={false}
									size="medium"
									sx={inlineInputSx}
									value={editedPhase.name}
									variant="outlined"
									onChange={(value) => handleInputChange("name", value)}
								/>
								{isNameDuplicate && (
									<div className={styles.errorMessage}>
										{translate("project-phases.validation.name-exists")}
									</div>
								)}
							</div>
						</CustomTableCell>
						<CustomTableCell
							className={styles.descriptionCell}
							data-testid={`phase.desc.cell.${phase?.id}`}
						>
							<LimitedTextField
								fullWidth
								multiline
								containerClassName={styles.inlineInputContainer}
								maxLength={1000}
								placeholder=""
								showCounter={false}
								showHelper={false}
								size="medium"
								sx={multilineInputSx}
								value={editedPhase.description}
								variant="outlined"
								onChange={(value) => handleInputChange("description", value)}
							/>
						</CustomTableCell>
						{isArchivedTab || phase.isArchived || phase.archived ? (
							<CustomTableCell className={styles.dateCell}>
								{phase?.archiveDate ? translateDate(phase?.archiveDate) : "-"}
							</CustomTableCell>
						) : (
							<>
								<CustomTableCell
									className={styles.dateCell}
									data-testid={`phase.start.date.${phase?.id}`}
								>
									<LimitedTextField
										fullWidth
										containerClassName={styles.inlineInputContainer}
										showCounter={false}
										showHelper={false}
										size="medium"
										sx={dateInputSx}
										type="date"
										value={editedPhase.startDate}
										variant="outlined"
										onChange={(value) => handleInputChange("startDate", value)}
									/>
								</CustomTableCell>
								<CustomTableCell
									className={styles.dateCell}
									data-testid={`phase.end.date.${phase?.id}`}
								>
									<LimitedTextField
										fullWidth
										containerClassName={styles.inlineInputContainer}
										error={isDateRangeInvalid}
										showCounter={false}
										showHelper={false}
										size="medium"
										sx={{
											...dateInputSx,
											...(isDateRangeInvalid && {
												"& .MuiOutlinedInput-root": {
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: "var(--red-500) !important",
													},
													"&:hover .MuiOutlinedInput-notchedOutline": {
														borderColor: "var(--red-500) !important",
													},
													"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
														borderColor: "var(--red-500) !important",
														borderWidth: "2px",
													},
												},
											}),
										}}
										type="date"
										value={editedPhase.endDate}
										variant="outlined"
										onChange={(value) => handleInputChange("endDate", value)}
									/>
								</CustomTableCell>
							</>
						)}
						<CustomTableCell className={styles.deliverablesCell}>
							{phase.deliverablesCount || "-"}
						</CustomTableCell>
						{isEditable && (
							<CustomTableCell align="right" className={styles.actionsCell}>
								<div className={styles.editActionsContainer}>
									<CustomButton
										className={styles.saveButton}
										disabled={
											isNameDuplicate ||
											isNameChecking ||
											isDateRangeInvalid ||
											!editedPhase.name?.trim()
										}
										size="sm"
										variant="contained"
										onClick={handleSave}
									>
										{translate("common:btn.save")}
									</CustomButton>
									<CustomButton
										className={styles.cancelButton}
										size="sm"
										variant="outlined"
										onClick={handleCancel}
									>
										{translate("common:btn.cancel")}
									</CustomButton>
								</div>
							</CustomTableCell>
						)}
					</CustomTableRow>
					<CustomDialog
						cancelLabel={translate("common:btn.cancel")}
						iconColor="var(--red-500)"
						iconModel={icon.faExclamationTriangle}
						open={confirmationDialog.open}
						submitColor={confirmationDialog.message === "delete" ? "destructive" : "primary"}
						submitLabel={translate("common:btn.confirm")}
						subTitle={confirmationDialog.message}
						title={confirmationDialog.title}
						onClose={closeConfirmationDialog}
						onSubmit={handleConfirmAction}
					/>
				</>
			);
		}
		return (
			<>
				<CustomTableRow
					ref={ref}
					{...provided?.draggableProps}
					{...(!phase.locked && isEditable ? provided?.dragHandleProps : {})}
					hover
					className={`${className} ${phase.locked ? styles.lockedRow : ""}`}
				>
					<CustomTableCell className={styles.orderNameCell}>
						<div className={styles.flexContainer}>
							<span>{phase.position + 1 || rowOrder + 1}</span> <span>{phase?.name}</span>
						</div>
					</CustomTableCell>
					<CustomTableCell className={styles.descriptionCell}>
						{renderExpandableText(phase?.description)}
					</CustomTableCell>
					{isArchivedTab || phase.isArchived || phase.archived ? (
						<CustomTableCell className={styles.dateCell}>
							{phase?.archiveDate ? translateDate(phase?.archiveDate) : "-"}
						</CustomTableCell>
					) : (
						<>
							<CustomTableCell className={styles.dateCell}>
								{phase?.startDate ? translateDate(phase?.startDate) : "-"}
							</CustomTableCell>
							<CustomTableCell className={styles.dateCell}>
								{phase?.endDate ? translateDate(phase?.endDate) : "-"}
							</CustomTableCell>
						</>
					)}
					<CustomTableCell className={styles.deliverablesCell}>
						<CustomButton
							variant="outlined"
							onClick={() => onClickOpenAssociated && onClickOpenAssociated(phase)}
						>
							{phase?.deliverablesCount || "-"}
						</CustomButton>
					</CustomTableCell>
					{isEditable && (
						<CustomTableCell align="right" className={styles.actionsCell}>
							<div className={styles.actionsContainer}>
								{isArchivedTab || phase.isArchived || !phase.active ? (
									<>
										{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
											isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) && (
												<CustomIconButton
													className={styles.actionButton}
													icon={icon.faHistory}
													tooltip={translate("project-phases.audit-journal")}
													variant="outlined"
													onClick={() => onClickHistory && onClickHistory(phase)}
												/>
											)}
										{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
											isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) && (
												<CustomIconButton
													className={styles.actionButton}
													data-testid={`phase.unarchive.btn.${phase?.id}`}
													icon={icon.faBoxArchive}
													tooltip={translate("project-phases.menu.unarchive")}
													variant="outlined"
													onClick={handleArchive}
												/>
											)}
									</>
								) : (
									<>
										{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
											isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) && (
												<CustomIconButton
													className={`${styles.actionButton} ${lockButtonClassName}`}
													color="secondary"
													data-testid={`phase.lock.btn.${phase?.id}`}
													icon={lockIcon}
													iconColor={lockIconColor}
													tooltip={lockTooltip}
													variant="outlined"
													onClick={handleLock}
												/>
											)}
										{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
											isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) && (
												<CustomIconButton
													className={styles.actionButton}
													data-testid={`phase.audit.btn.${phase?.id}`}
													icon={icon.faHistory}
													tooltip={translate("project-phases.audit-journal")}
													variant="outlined"
													onClick={() => onClickHistory && onClickHistory(phase)}
												/>
											)}
										<CustomIconButton
											ref={anchorRef}
											className={styles.actionButton}
											data-testid={`phase.action.btn.${phase?.id}`}
											disabled={!!phase.locked}
											icon={icon.faEllipsisH}
											variant="outlined"
											onClick={handleToggleDropdown}
										/>
										{!phase.locked && (
											<DropDown
												ref={anchorRef}
												className={styles.dropdownMenu}
												open={openDropdown}
												onClose={handleCloseDropdown}
											>
												<MenuList>
													<MenuItem
														className={styles.menuItem}
														disabled={
															editingPhaseId !== null && editingPhaseId !== phase.id
														}
														onClick={handleEdit}
													>
														<IconComponent
															className={styles.menuItemIcon}
															icon={icon.faPen}
															size="2xs"
														/>
														{translate("project-phases.menu.edit")}
													</MenuItem>
													<MenuItem
														className={styles.menuItem}
														disabled={phase.deliverablesCount > 0}
														onClick={handleDelete}
													>
														<IconComponent
															className={styles.menuItemIcon}
															icon={icon.faTrash}
															size="2xs"
														/>
														{translate("project-phases.menu.delete")}
													</MenuItem>
													{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
														isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) && (
															<MenuItem
																className={styles.menuItem}
																onClick={handleArchive}
															>
																<IconComponent
																	className={styles.menuItemIcon}
																	icon={icon.faBoxArchive}
																	size="2xs"
																/>
																{translate("project-phases.menu.archive")}
															</MenuItem>
														)}
													{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
														isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) && (
															<Divider />
														)}
													{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
														isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) && (
															<MenuItem
																className={`${styles.menuItem}`}
																disabled={phase.deliverablesCount === 0}
																onClick={handleExport}
															>
																<IconComponent
																	className={styles.menuItemIcon}
																	icon={icon.faUpload}
																	size="2xs"
																/>
																{translate("project-phases.menu.export-strategy")}
															</MenuItem>
														)}
													{isFeatureEnabled(FLAGS.COVERAGE_PHASE_LOCK) &&
														isSegFeatureEnabled(SegFlags.COVERAGE_PHASE_LOCK) &&
														!phase.imported && (
															<MenuItem
																className={`${styles.menuItem}`}
																onClick={handleImportHeritage}
															>
																<IconComponent
																	className={styles.menuItemIcon}
																	icon={icon.faDownload}
																	size="2xs"
																/>
																{translate("project-phases.menu.import-heritage")}
															</MenuItem>
														)}
												</MenuList>
											</DropDown>
										)}
									</>
								)}
							</div>
						</CustomTableCell>
					)}
				</CustomTableRow>
				<CustomDialog
					cancelLabel={translate("common:btn.cancel")}
					iconColor="var(--red-500)"
					iconModel={icon.faExclamationTriangle}
					open={confirmationDialog.open}
					submitLabel={translate("common:btn.confirm")}
					subTitle={confirmationDialog.message}
					title={confirmationDialog.title}
					onClose={closeConfirmationDialog}
					onSubmit={handleConfirmAction}
				/>
			</>
		);
	}
);

PhaseRow.displayName = "PhaseRow";

export default PhaseRow;
